import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { qolConcernsAPI, contentGroupsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const PHASE_OPTIONS = [
  { value: 'chemotherapy', label: 'Chemotherapy' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'radiation', label: 'Radiation' },
  { value: 'her2_therapy', label: 'HER2 Therapy' },
  { value: 'cdk46_therapy', label: 'CDK4/6 Therapy' },
  { value: 'endocrine_therapy', label: 'Endocrine Therapy' },
  { value: 'survivorship', label: 'Survivorship' },
]

const EVIDENCE_LEVELS = [
  { value: 'strong', label: 'Strong' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'limited', label: 'Limited' },
  { value: 'strong_moderate', label: 'Strong-Moderate' },
]

const evidenceStyles = {
  strong: '#16a34a',
  strong_moderate: '#65a30d',
  moderate: '#d97706',
  limited: '#71717a',
}

// Normalize list responses to an array regardless of envelope shape
const toList = (data, key) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.[key])) return data[key]
  if (Array.isArray(data?.data)) return data.data
  return []
}

const arrayToCsv = (arr) => (Array.isArray(arr) ? arr.join(', ') : '')
const csvToArray = (str) =>
  (str || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)

const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '')
const linesToArray = (str) =>
  (str || '')
    .split('\n')
    .map((v) => v.trim())
    .filter(Boolean)

const EMPTY_FORM = {
  group_id: '',
  source_number: '',
  phase: 'chemotherapy',
  concern: '',
  aka: '',
  mechanism: '',
  tier1_tips: '',
  tier2_escalation: '',
  evidence_level: 'moderate',
  evidence_notes: '',
  keywords: '',
  applicable_treatments: '',
  timing: 'any',
  advice_generalizes: true,
  is_active: true,
}

const TREATMENT_TOKENS = ['chemotherapy', 'radiation', 'surgery', 'her2_therapy', 'cdk46_therapy', 'endocrine_therapy']
const TIMING_OPTIONS = [
  { value: 'during', label: 'During treatment' },
  { value: 'after', label: 'After treatment' },
  { value: 'any', label: 'Any time' },
]

function QolConcerns() {
  const { hasRole } = useAuth()
  const canEdit = hasRole(['system_admin', 'administrator', 'clinical_designer'])
  const [concerns, setConcerns] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [filterGroup, setFilterGroup] = useState('')
  const [filterPhase, setFilterPhase] = useState('')

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingConcern, setEditingConcern] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [concernsResponse, groupsResponse] = await Promise.all([
        qolConcernsAPI.getAll(),
        contentGroupsAPI.getAll(true),
      ])
      setConcerns(toList(concernsResponse.data, 'qol_concerns'))
      setGroups(toList(groupsResponse.data, 'content_groups'))
    } catch (err) {
      console.error('Error fetching QOL concerns:', err)
      setError('Failed to load QOL concerns. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const groupName = (groupId) => groups.find((g) => g.id === groupId)?.name || '—'

  const filteredConcerns = concerns.filter((concern) => {
    const matchesSearch =
      !searchTerm ||
      concern.concern?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      concern.aka?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (concern.keywords || []).some((k) => k.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesGroup = !filterGroup || concern.group_id === filterGroup
    const matchesPhase = !filterPhase || concern.phase === filterPhase
    return matchesSearch && matchesGroup && matchesPhase
  })

  const handleOpenDialog = (concern = null) => {
    if (concern) {
      setEditingConcern(concern)
      setFormData({
        group_id: concern.group_id || '',
        source_number: concern.source_number ?? '',
        phase: concern.phase || 'chemotherapy',
        concern: concern.concern || '',
        aka: concern.aka || '',
        mechanism: concern.mechanism || '',
        tier1_tips: arrayToLines(concern.tier1_tips),
        tier2_escalation: concern.tier2_escalation || '',
        evidence_level: concern.evidence_level || 'moderate',
        evidence_notes: concern.evidence_notes || '',
        keywords: arrayToCsv(concern.keywords),
        applicable_treatments: arrayToCsv(concern.applicable_treatments),
        timing: concern.timing || 'any',
        advice_generalizes: concern.advice_generalizes !== false,
        is_active: concern.is_active !== false,
      })
    } else {
      setEditingConcern(null)
      setFormData(EMPTY_FORM)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingConcern(null)
    setFormData(EMPTY_FORM)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const payload = {
        group_id: formData.group_id || null,
        source_number: formData.source_number === '' ? null : Number(formData.source_number),
        phase: formData.phase,
        concern: formData.concern,
        aka: formData.aka || null,
        mechanism: formData.mechanism || null,
        tier1_tips: linesToArray(formData.tier1_tips),
        tier2_escalation: formData.tier2_escalation,
        evidence_level: formData.evidence_level,
        evidence_notes: formData.evidence_notes || null,
        keywords: csvToArray(formData.keywords),
        applicable_treatments: csvToArray(formData.applicable_treatments),
        timing: formData.timing,
        advice_generalizes: formData.advice_generalizes,
        is_active: formData.is_active,
      }
      if (editingConcern) {
        await qolConcernsAPI.update(editingConcern.id, payload)
      } else {
        await qolConcernsAPI.create(payload)
      }
      handleCloseDialog()
      fetchData()
    } catch (err) {
      console.error('Error saving QOL concern:', err)
      setError(err.response?.data?.message || 'Failed to save QOL concern. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field) => (e) => setFormData({ ...formData, [field]: e.target.value })

  const columns = [
    {
      field: 'source_number',
      headerName: '#',
      width: 60,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
          {params.value ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'phase',
      headerName: 'Phase',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontSize="0.8125rem">
          {PHASE_OPTIONS.find((p) => p.value === params.value)?.label || params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'concern',
      headerName: 'Concern',
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.concern}
          </Typography>
          {params.row.aka && (
            <Typography variant="caption" color="text.secondary" fontSize="0.6875rem">
              {params.row.aka}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'evidence_level',
      headerName: 'Evidence',
      width: 130,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontSize="0.8125rem"
          sx={{ color: evidenceStyles[params.value] || 'text.secondary' }}
        >
          {EVIDENCE_LEVELS.find((l) => l.value === params.value)?.label || params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'group_id',
      headerName: 'Group',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
          {groupName(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={canEdit ? 'Edit' : 'View'}>
          <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
            {canEdit ? <EditIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">QOL Concerns</Typography>
          <Typography variant="body2" color="text.secondary">
            Quality-of-life concerns by treatment phase, with tiered self-management tips and
            escalation guidance.
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Concern
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search concerns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Group</InputLabel>
            <Select value={filterGroup} label="Group" onChange={(e) => setFilterGroup(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Phase</InputLabel>
            <Select value={filterPhase} label="Phase" onChange={(e) => setFilterPhase(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {PHASE_OPTIONS.map((phase) => (
                <MenuItem key={phase.value} value={phase.value}>
                  {phase.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredConcerns.length} of {concerns.length} concerns
          </Typography>
        </Box>
      </Paper>

      {/* Concern List */}
      <Paper>
        <DataGrid
          rows={filteredConcerns}
          columns={columns}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50]}
          autoHeight
          disableSelectionOnClick
          getRowId={(row) => row.id}
          onRowDoubleClick={(params) => handleOpenDialog(params.row)}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConcern ? (canEdit ? 'Edit QOL Concern' : 'View QOL Concern') : 'Create QOL Concern'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Group</InputLabel>
                <Select value={formData.group_id} label="Group" onChange={updateField('group_id')}>
                  <MenuItem value="">None</MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Source Number"
                type="number"
                value={formData.source_number}
                onChange={updateField('source_number')}
                margin="normal"
                disabled={!canEdit}
                helperText="Row number in the source document"
              />
              <FormControl fullWidth margin="normal" disabled={!canEdit} required>
                <InputLabel>Phase</InputLabel>
                <Select value={formData.phase} label="Phase" onChange={updateField('phase')}>
                  {PHASE_OPTIONS.map((phase) => (
                    <MenuItem key={phase.value} value={phase.value}>
                      {phase.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Concern"
                value={formData.concern}
                onChange={updateField('concern')}
                margin="normal"
                required
                disabled={!canEdit}
              />
              <TextField
                fullWidth
                label="Also known as"
                value={formData.aka}
                onChange={updateField('aka')}
                margin="normal"
                disabled={!canEdit}
              />
            </Box>
            <TextField
              fullWidth
              label="Mechanism"
              value={formData.mechanism}
              onChange={updateField('mechanism')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
              helperText="Why this concern occurs during this phase"
            />
            <TextField
              fullWidth
              label="Tier 1 Tips"
              value={formData.tier1_tips}
              onChange={updateField('tier1_tips')}
              margin="normal"
              multiline
              rows={5}
              disabled={!canEdit}
              helperText="One tip per line — self-management guidance offered first"
            />
            <TextField
              fullWidth
              label="Tier 2 Escalation"
              value={formData.tier2_escalation}
              onChange={updateField('tier2_escalation')}
              margin="normal"
              multiline
              rows={3}
              required
              disabled={!canEdit}
              helperText="Required — when and how to escalate to the care team"
            />
            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Evidence Level</InputLabel>
                <Select
                  value={formData.evidence_level}
                  label="Evidence Level"
                  onChange={updateField('evidence_level')}
                >
                  {EVIDENCE_LEVELS.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Keywords"
                value={formData.keywords}
                onChange={updateField('keywords')}
                margin="normal"
                disabled={!canEdit}
                helperText="Comma-separated matching terms"
              />
            </Box>
            <TextField
              fullWidth
              label="Evidence Notes"
              value={formData.evidence_notes}
              onChange={updateField('evidence_notes')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
            />
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Applicable Treatments"
                value={formData.applicable_treatments}
                onChange={updateField('applicable_treatments')}
                margin="normal"
                disabled={!canEdit}
                helperText={`Comma-separated; empty = applies regardless of treatment. Tokens: ${TREATMENT_TOKENS.join(', ')}`}
              />
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Timing</InputLabel>
                <Select
                  value={formData.timing}
                  label="Timing"
                  onChange={updateField('timing')}
                >
                  {TIMING_OPTIONS.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.advice_generalizes}
                  onChange={(e) => setFormData({ ...formData, advice_generalizes: e.target.checked })}
                  disabled={!canEdit}
                />
              }
              label="Self-care tips generalize beyond the tagged treatment"
              sx={{ mt: 1, display: 'block' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={!canEdit}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.concern || !formData.tier2_escalation}
            >
              {saving ? 'Saving...' : editingConcern ? 'Update' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default QolConcerns
